import { FC } from 'react';
import { decode } from 'he';
import { Highlight } from 'react-instantsearch';
import { sliceWithoutCuttingWords } from '@belong/type-utils';
import { Heading, Stack } from '@belong/ui-components';
import { List } from './List';
import { SEARCH_TEST_IDS } from './test/utils/testIds';
import { ISearchHitGroupProps } from './types';

const CustomSnippet = ({ hit, attribute, maxLength, highlightedTagName }) => {
  // eslint-disable-next-line no-underscore-dangle
  const snippetResult = hit._snippetResult[attribute] ? hit._snippetResult[attribute] : '';
  const isHighlighted = snippetResult && snippetResult.matchLevel === 'full';
  const value = isHighlighted ? snippetResult.value : hit[attribute];

  let snippet = '';
  if (value) {
    // If there's highlighted text, use it. Otherwise, use a substring of the original text.
    snippet = isHighlighted ? value : sliceWithoutCuttingWords(value, maxLength);
  }

  // Replace the <mark> tags with the custom tag provided
  const highlightedSnippet = decode(snippet)
    .replaceAll('<mark>', `<${highlightedTagName}>`)
    .replaceAll('</mark>', `</${highlightedTagName}>`);

  return <div dangerouslySetInnerHTML={{ __html: highlightedSnippet }} />;
};

export const SearchHitGroup: FC<ISearchHitGroupProps> = ({ hits, start, end, page }) => {
  const transformItems = items => {
    return items.map(item => ({
      id: item.objectID,
      heading: <Highlight attribute={['hierarchy.lvl1']} hit={item} highlightedTagName="em" />,
      link: item.url_without_anchor,
      description: <CustomSnippet attribute="content" hit={item} highlightedTagName="em" maxLength="180" />
    }));
  };

  const id = `${SEARCH_TEST_IDS.RESULTS_GROUP_HEADING}-${page}`;

  return (
    <Stack>
      <Heading variant="small" as="h3" data-testid={id} id={id} tabIndex={-1}>
        {`Results ${start} to ${end}`}
      </Heading>
      <List items={transformItems(hits)} ariaLabelledby={id} />
    </Stack>
  );
};

SearchHitGroup.displayName = 'HitGroup';
